import React, { useState, useEffect } from 'react';
import './NBATimeMachine.css';
import './DualPlayerTheater.css';
import {
    EnrichedMustWatchCard,
    EnrichedLegendaryMatchupCard,
    EnrichedDiscoveryModeCard,
    EnrichedGameCard
} from './SharedGameEnrichment';
import AppleDiscoveryModes from './AppleDiscoveryModes';
import AppleSeasonInsights from './AppleSeasonInsights';

const getApiBaseUrl = () => {
    // Check if we're in development or production
    if (process.env.NODE_ENV === 'development') {
        return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    }
    
    // In production, use relative paths or environment variable
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const NBATimeMachine = ({ player, season, seasonType: initialSeasonType, onGameSelect }) => {
    const [enhancedData, setEnhancedData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeMode, setActiveMode] = useState('matchups');
    const [selectedSeason, setSelectedSeason] = useState(season || '2014-15');
    const [seasonType, setSeasonType] = useState(initialSeasonType || 'Regular Season + Playoffs');
    const [isDualPlayerMode, setIsDualPlayerMode] = useState(false);
    const [coStarPlayer, setCoStarPlayer] = useState(null);
    const [theaterData, setTheaterData] = useState(null);
    const [theaterLoading, setTheaterLoading] = useState(false);
    
    // Tag filtering state
    const [activeTagFilters, setActiveTagFilters] = useState({
        careerStage: [],
        eliteCategory: [],
        narrativeType: [],
        matchupTier: []
    });
    const [showAllFilters, setShowAllFilters] = useState(false);

    const seasons = [
        '2024-25', '2023-24', '2022-23', '2021-22', '2020-21', '2019-20',
        '2018-19', '2017-18', '2016-17', '2015-16', '2014-15', '2013-14',
        '2012-13', '2011-12', '2010-11', '2009-10', '2008-09', '2007-08'
    ];

    const seasonTypes = ['Regular Season + Playoffs', 'Playoffs', 'Pre Season'];

    // Filter options organized by priority (most important shown first)
    const filterOptions = {
        // Primary filters - always visible
        primary: {
            matchupTier: [
                { id: 'legendary', label: '🏆 Legendary', description: '8.0+ excitement score' },
                { id: 'compelling', label: '⚡ Compelling', description: '5.0+ excitement score' },
                { id: 'watchable', label: '👀 Watchable', description: '3.0+ excitement score' }
            ],
            careerStage: [
                { id: 'superstar', label: '🔥 Superstar', description: '28+ PPG elite players' },
                { id: 'allstar', label: '⭐ All-Star', description: '25+ PPG prime players' },
                { id: 'risingstar', label: '🚀 Rising Star', description: '22+ PPG emerging talent' }
            ]
        },
        // Secondary filters - collapsible
        secondary: {
            eliteCategory: [
                { id: 'dualthreat', label: '🎭 Dual Threat', description: 'Scoring + Playmaking leaders' },
                { id: 'twowayforce', label: '⚔️ Two-Way Force', description: 'Scoring + Rebounding leaders' },
                { id: 'completeplayer', label: '🔄 Complete Player', description: 'Playmaking + Rebounding' },
                { id: 'scoringmachine', label: '🔥 Scoring Machine', description: '30+ PPG scorers' },
                { id: 'maestro', label: '⚡ Maestro', description: '9+ APG playmakers' },
                { id: 'distributor', label: '🎮 Distributor', description: '6+ APG facilitators' },
                { id: 'boardbeast', label: '💪 Board Beast', description: '12+ RPG rebounders' }
            ],
            narrativeType: [
                { id: 'rivalry', label: '⚔️ Rivalry', description: 'Historic team rivalries' },
                { id: 'homecoming', label: '🏠 Homecoming', description: 'Playing former team' },
                { id: 'milestone', label: '🏆 Milestone', description: '50+ pts, triple-doubles' },
                { id: 'playoffimplications', label: '🎯 Playoff Race', description: 'Late season crucial games' },
                { id: 'draftclass', label: '👥 Draft Class', description: 'Same draft year battles' }
            ],
            careerStageExtended: [
                { id: 'breakout', label: '📈 Breakout', description: '18+ PPG emerging players' },
                { id: 'veteranstar', label: '👑 Veteran Star', description: '20+ PPG veterans' },
                { id: 'courtvet', label: '🧠 Court Veteran', description: 'High IQ veterans' }
            ]
        }
    };

    useEffect(() => {
        if (player && selectedSeason) {
            fetchEnhancedData();
        }
    }, [player, selectedSeason, seasonType]);

    // Tag filtering functions
    const toggleTagFilter = (category, tagId) => {
        setActiveTagFilters(prev => ({
            ...prev,
            [category]: prev[category].includes(tagId)
                ? prev[category].filter(id => id !== tagId)
                : [...prev[category], tagId]
        }));
    };

    const clearAllFilters = () => {
        setActiveTagFilters({
            careerStage: [],
            eliteCategory: [],
            narrativeType: [],
            matchupTier: []
        });
    };

    const getPlayerTag = (opponent) => {
        const stats = opponent.stats || {};
        const status = opponent.status || 'prime';
        const pts = stats.pts || 0;
        const ast = stats.ast || 0;
        
        if (status === 'prime') {
            if (pts >= 28) return 'superstar';
            if (pts >= 25) return 'allstar';
            if (ast >= 8) return 'floorgeneral';
        } else if (status === 'emerging') {
            if (pts >= 22) return 'risingstar';
            if (pts >= 18) return 'breakout';
        } else if (status === 'veteran') {
            if (pts >= 20) return 'veteranstar';
            if (ast >= 7) return 'courtvet';
        }
        return status;
    };

    // Helper to get all filter categories (primary + secondary)
    const getAllFilterCategories = () => {
        const allCategories = {};
        Object.entries(filterOptions.primary).forEach(([key, value]) => {
            allCategories[key] = value;
        });
        Object.entries(filterOptions.secondary).forEach(([key, value]) => {
            if (key === 'careerStageExtended') {
                // Merge extended career stage with primary career stage
                allCategories.careerStage = [...allCategories.careerStage, ...value];
            } else {
                allCategories[key] = value;
            }
        });
        return allCategories;
    };

    const getCategoryTag = (opponent) => {
        const reasons = opponent.elite_reasons || [];
        const overallStatus = opponent.overall_status || 'star';
        const stats = opponent.stats || {};
        const pts = stats.pts || 0;
        const ast = stats.ast || 0;
        const reb = stats.reb || 0;

        if (overallStatus === 'multi_category_elite') {
            if (reasons.includes('scoring_leader') && reasons.includes('playmaking_leader')) {
                return 'dualthreat';
            }
            if (reasons.includes('scoring_leader') && reasons.includes('rebounding_leader')) {
                return 'twowayforce';
            }
            if (reasons.includes('playmaking_leader') && reasons.includes('rebounding_leader')) {
                return 'completeplayer';
            }
        }
        
        if (overallStatus === 'offensive_star') {
            if (pts >= 30) return 'scoringmachine';
        }
        
        if (overallStatus === 'playmaker') {
            if (ast >= 9) return 'maestro';
            if (ast >= 6) return 'distributor';
        }
        
        if (overallStatus === 'rebounder') {
            if (reb >= 12) return 'boardbeast';
        }
        
        return overallStatus;
    };

    const matchesFilters = (game) => {
        if (!game) return true;
        
        const hasActiveFilters = Object.values(activeTagFilters).some(filters => filters.length > 0);
        if (!hasActiveFilters) return true;

        // Check matchup tier filter
        if (activeTagFilters.matchupTier.length > 0) {
            const matchupTier = game.matchup_analysis?.matchup_tier || 'watchable';
            if (!activeTagFilters.matchupTier.includes(matchupTier)) {
                return false;
            }
        }

        // Check narrative type filter
        if (activeTagFilters.narrativeType.length > 0) {
            const narrativeTypes = game.matchup_analysis?.narrative_types || [];
            const hasMatchingNarrative = activeTagFilters.narrativeType.some(filter => 
                narrativeTypes.includes(filter)
            );
            if (!hasMatchingNarrative) {
                return false;
            }
        }

        // Check opponent tags (career stage and elite category)
        if (activeTagFilters.careerStage.length > 0 || activeTagFilters.eliteCategory.length > 0) {
            const opponentStars = game.opponent_stars || [];
            if (opponentStars.length === 0) return false;

            const hasMatchingOpponent = opponentStars.some(opponent => {
                const playerTag = getPlayerTag(opponent);
                const categoryTag = getCategoryTag(opponent);
                
                const matchesCareerStage = activeTagFilters.careerStage.length === 0 || 
                    activeTagFilters.careerStage.includes(playerTag);
                const matchesEliteCategory = activeTagFilters.eliteCategory.length === 0 || 
                    activeTagFilters.eliteCategory.includes(categoryTag);
                
                return matchesCareerStage && matchesEliteCategory;
            });
            
            if (!hasMatchingOpponent) {
                return false;
            }
        }

        return true;
    };

    const fetchEnhancedData = async () => {
        setLoading(true);
        setError(null);
        setEnhancedData(null); // Clear previous data for better page navigation

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/v1/dev/player-logs/player/${player.id}/enhanced-logs?` +
                `player_name=${encodeURIComponent(player.name)}&` +
                `season=${encodeURIComponent(selectedSeason)}&` +
                `season_type=${encodeURIComponent(seasonType)}`
            );

            if (!response.ok) {
                // Try to get detailed error message from API
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || `Failed to fetch enhanced data: ${response.statusText}`);
                } catch (parseError) {
                    throw new Error(`Failed to fetch enhanced data: ${response.statusText}`);
                }
            }

            const data = await response.json();
            setEnhancedData(data);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching enhanced data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDualPlayerTheater = async () => {
        if (!coStarPlayer) return;
        
        setTheaterLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/v1/dev/player-logs/dual-player-theater?` +
                `player_a_id=${player.id}&` +
                `player_b_id=${coStarPlayer.id}&` +
                `player_a_name=${encodeURIComponent(player.name)}&` +
                `player_b_name=${encodeURIComponent(coStarPlayer.name)}&` +
                `season=${encodeURIComponent(selectedSeason)}&` +
                `season_type=${encodeURIComponent(seasonType)}`
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch dual-player theater: ${response.statusText}`);
            }

            const data = await response.json();
            setTheaterData(data);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching dual-player theater:', err);
        } finally {
            setTheaterLoading(false);
        }
    };

    // Re-fetch theater data when dual-player mode is enabled and dependencies change
    useEffect(() => {
        if (isDualPlayerMode && coStarPlayer && selectedSeason) {
            fetchDualPlayerTheater();
        }
    }, [isDualPlayerMode, coStarPlayer, selectedSeason, seasonType]);

    const WatchabilityIndicator = ({ tier, score }) => {
        const indicators = {
            'must_watch': { emoji: '🔥🔥🔥', label: 'Must Watch' },
            'excellent': { emoji: '🔥🔥', label: 'Excellent' },
            'very_good': { emoji: '🔥', label: 'Very Good' },
            'good': { emoji: '⭐', label: 'Good' },
            'watchable': { emoji: '👀', label: 'Watchable' }
        };

        const indicator = indicators[tier] || indicators['watchable'];

        return (
            <div className="watchability-indicator">
                <span className="watchability-emoji">{indicator.emoji}</span>
                <span className="watchability-score">{score}</span>
                <span className="watchability-label">{indicator.label}</span>
            </div>
        );
    };

    // Old LegendaryMatchupCard component removed - now using EnrichedLegendaryMatchupCard

    // Old MustWatchCard component removed - now using EnrichedMustWatchCard

    // Old DiscoveryModeCard component removed - now using EnrichedDiscoveryModeCard

    // Old GameCard component removed - now using EnrichedGameCard

    const DiscoveryMode = ({ mode, games, title, description }) => (
        <div className="discovery-mode">
            <div className="mode-header">
                <h3>{title}</h3>
                <p>{description}</p>
                <span className="game-count">{(games || []).length} games</span>
            </div>
            <div className="discovery-mode-grid">
                {(games || []).slice(0, 6).map((game, idx) => (
                    <EnrichedDiscoveryModeCard key={idx} game={game} modeType={mode} />
                ))}
            </div>
        </div>
    );

    // Helper function to generate narrative text
    const generateNarrative = (game, playerA, playerB) => {
        const ptsA = game.player_a_stats?.pts || 0;
        const ptsB = game.player_b_stats?.pts || 0;
        const astA = game.player_a_stats?.ast || 0;
        const astB = game.player_b_stats?.ast || 0;
        const rebA = game.player_a_stats?.reb || 0;
        const rebB = game.player_b_stats?.reb || 0;
        
        if (game.relationship === 'opponents') {
            if (ptsA > 30 && ptsB > 30) {
                return `Epic duel: ${playerA}'s ${ptsA}pts vs ${playerB}'s ${ptsB}pts`;
            } else if (ptsA > ptsB + 10) {
                return `${playerA} dominates with ${ptsA}pts over ${playerB}`;
            } else if (ptsB > ptsA + 10) {
                return `${playerB} outshines with ${ptsB}pts against ${playerA}`;
            } else {
                return `Competitive battle: ${playerA} (${ptsA}pts) vs ${playerB} (${ptsB}pts)`;
            }
        } else {
            const totalPts = ptsA + ptsB;
            if (totalPts > 60) {
                return `Combined offensive showcase: ${totalPts} points`;
            } else if (astA + astB > 20) {
                return `Playmaking clinic: ${astA + astB} combined assists`;
            } else {
                return `Solid performances on the same night`;
            }
        }
    };
    
    // Helper function to determine watchability tier
    const getWatchabilityTier = (combinedScore) => {
        if (combinedScore >= 16) return { tier: 'MUST WATCH', emoji: '🔥' };
        if (combinedScore >= 14) return { tier: 'EXCELLENT', emoji: '⚡' };
        if (combinedScore >= 12) return { tier: 'COMPELLING', emoji: '✨' };
        if (combinedScore >= 10) return { tier: 'VERY GOOD', emoji: '👍' };
        return { tier: 'GOOD', emoji: '📺' };
    };
    
    // Helper function to get performance level
    const getPerformanceLevel = (score) => {
        if (score >= 9) return 'LEGENDARY';
        if (score >= 7) return 'ELITE';
        if (score >= 5) return 'EXCELLENT';
        if (score >= 3) return 'VERY GOOD';
        return 'GOOD';
    };

    const DualPlayerTheaterCard = ({ dualGame, category, showNarrative, showWatchability }) => {
        const categoryIcons = {
            'entertainment': '🏆',
            'head_to_head': '⚔️',
            'head_to_head_power_ranking': '⚔️',
            'best_performance': '🌟',
            'gladiator_battles': '⚔️',
            'rival_matchups': '🔥',
            'clutch_duels': '💎',
            'breakout_games': '🌟',
            'rookie_showcases': '🚀',
            'prime_battles': '🏆',
            'narrative': '🎭',
            'must_watch': '🔥'
        };

        const categoryColors = {
            'entertainment': '#ff9500',
            'head_to_head': '#ff453a',
            'head_to_head_power_ranking': '#ff453a',
            'best_performance': '#0a84ff',
            'gladiator_battles': '#ff453a',
            'rival_matchups': '#30d158',
            'clutch_duels': '#0a84ff',
            'breakout_games': '#ffcc00',
            'rookie_showcases': '#bf5af2',
            'prime_battles': '#ff6b35',
            'narrative': '#bf5af2',
            'must_watch': '#ff453a'
        };
        
        const combinedScore = (dualGame.player_a_score || 0) + (dualGame.player_b_score || 0);
        const watchability = getWatchabilityTier(combinedScore);

        return (
            <div className={`dual-game-card ${category}`} style={{ borderLeftColor: categoryColors[category] }}>
                <div className="dual-card-header">
                    <div className="matchup-info">
                        <div className="game-date">{dualGame.game_date}</div>
                        <div className="matchup-text">{dualGame.matchup_description || `${dualGame.player_a} vs ${dualGame.player_b}`}</div>
                    </div>
                    {showWatchability && (
                        <div className="watchability-badge">
                            {watchability.emoji} {watchability.tier}
                        </div>
                    )}
                </div>
                
                {(showNarrative && dualGame.narrative) && (
                    <div className="narrative-section">
                        <div className="narrative-label">🎭 The Battle</div>
                        <div className="narrative-text">{dualGame.narrative}</div>
                    </div>
                )}

                <div className="dual-performance">
                    <div className="player-performance">
                        {/* Player A Photo */}
                        {theaterData?.theater_analysis?.player_a_visuals?.photo_url && (
                            <div className="player-photo-container">
                                <img 
                                    src={theaterData.theater_analysis.player_a_visuals.photo_url} 
                                    alt={dualGame.player_a}
                                    className="dual-player-photo"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}
                        <h5>{dualGame.player_a}</h5>
                        <div className="performance-level">
                            Level: {getPerformanceLevel(dualGame.player_a_score || 0)}
                        </div>
                        <div className="stat-line">
                            <span className="pts">{dualGame.player_a_stats?.pts || 0} PTS</span>
                            <span className="reb">{dualGame.player_a_stats?.reb || 0} REB</span>
                            <span className="ast">{dualGame.player_a_stats?.ast || 0} AST</span>
                        </div>
                        <div className="excitement-score">
                            🔥 Excitement Score: {Math.round(dualGame.player_a_score || 0)}/10
                        </div>
                    </div>

                    <div className="vs-divider">{dualGame.relationship === 'opponents' ? 'VS' : '&'}</div>

                    <div className="player-performance">
                        {/* Player B Photo */}
                        {theaterData?.theater_analysis?.player_b_visuals?.photo_url && (
                            <div className="player-photo-container">
                                <img 
                                    src={theaterData.theater_analysis.player_b_visuals.photo_url} 
                                    alt={dualGame.player_b}
                                    className="dual-player-photo"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}
                        <h5>{dualGame.player_b}</h5>
                        <div className="performance-level">
                            Level: {getPerformanceLevel(dualGame.player_b_score || 0)}
                        </div>
                        <div className="stat-line">
                            <span className="pts">{dualGame.player_b_stats?.pts || 0} PTS</span>
                            <span className="reb">{dualGame.player_b_stats?.reb || 0} REB</span>
                            <span className="ast">{dualGame.player_b_stats?.ast || 0} AST</span>
                        </div>
                        <div className="excitement-score">
                            🔥 Excitement Score: {Math.round(dualGame.player_b_score || 0)}/10
                        </div>
                    </div>
                </div>

                {/* Game Details Section - Starting Lineups and Top Scorers */}
                {dualGame.game_details && (
                    <div className="game-details-section">
                        <div className="game-details-header">
                            <span className="details-icon">🏀</span>
                            <span className="details-title">Game Details</span>
                        </div>
                        
                        <div className="teams-details">
                            {/* Away Team (Left - Primary Player's Team) */}
                            <div className="team-details">
                                <div className="team-header">
                                    {dualGame.game_details.home_team?.logo && (
                                        <img 
                                            src={dualGame.game_details.home_team.logo} 
                                            alt={`${dualGame.game_details.home_team.name} logo`}
                                            className="team-logo"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    )}
                                    <h6 className="team-name">{dualGame.game_details.home_team?.name || 'Home Team'}</h6>
                                </div>
                                
                                {/* Starting Lineup */}
                                <div className="lineup-section">
                                    <div className="section-title">Starting Lineup</div>
                                    <div className="lineup-grid">
                                        {dualGame.game_details.away_team?.starting_lineup?.slice(0, 6).map((player, idx) => (
                                            <div key={idx} className={`lineup-player ${player.is_sixth_man ? 'sixth-man' : ''}`}>
                                                <div className="player-name">
                                                    {player.player_name}
                                                    {player.is_sixth_man && <span className="sixth-man-badge">6TH</span>}
                                                </div>
                                                <div className="player-position">{player.position}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Top Scorers */}
                                <div className="scorers-section">
                                    <div className="section-title">Top Scorers</div>
                                    <div className="scorers-list">
                                        {dualGame.game_details.away_team?.top_scorers?.map((player, idx) => (
                                            <div key={idx} className="scorer-player">
                                                <div className="scorer-rank">#{idx + 1}</div>
                                                <div className="scorer-info">
                                                    <div className="scorer-name">{player.player_name}</div>
                                                    <div className="scorer-stats">
                                                        {player.points}PTS • {player.rebounds}REB • {player.assists}AST
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Home Team (Right - Co-Star's Team) */}
                            <div className="team-details">
                                <div className="team-header">
                                    {dualGame.game_details.away_team?.logo && (
                                        <img 
                                            src={dualGame.game_details.away_team.logo} 
                                            alt={`${dualGame.game_details.away_team.name} logo`}
                                            className="team-logo"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    )}
                                    <h6 className="team-name">{dualGame.game_details.away_team?.name || 'Away Team'}</h6>
                                </div>
                                
                                {/* Starting Lineup */}
                                <div className="lineup-section">
                                    <div className="section-title">Starting Lineup</div>
                                    <div className="lineup-grid">
                                        {dualGame.game_details.home_team?.starting_lineup?.slice(0, 6).map((player, idx) => (
                                            <div key={idx} className={`lineup-player ${player.is_sixth_man ? 'sixth-man' : ''}`}>
                                                <div className="player-name">
                                                    {player.player_name}
                                                    {player.is_sixth_man && <span className="sixth-man-badge">6TH</span>}
                                                </div>
                                                <div className="player-position">{player.position}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Top Scorers */}
                                <div className="scorers-section">
                                    <div className="section-title">Top Scorers</div>
                                    <div className="scorers-list">
                                        {dualGame.game_details.home_team?.top_scorers?.map((player, idx) => (
                                            <div key={idx} className="scorer-player">
                                                <div className="scorer-rank">#{idx + 1}</div>
                                                <div className="scorer-info">
                                                    <div className="scorer-name">{player.player_name}</div>
                                                    <div className="scorer-stats">
                                                        {player.points}PTS • {player.rebounds}REB • {player.assists}AST
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Game Competitiveness Section */}
                {dualGame.game_details?.competitiveness && (
                    <div className="competitiveness-section">
                        <div className="competitiveness-header">
                            <span className="competitiveness-icon">🎯</span>
                            <span className="competitiveness-title">Game Quality</span>
                        </div>
                        
                        <div className="competitiveness-content">
                            <div className="competitiveness-score">
                                <div className="score-display">
                                    <span className="score-number">{dualGame.game_details.competitiveness.competitiveness_score}</span>
                                    <span className="score-max">/10</span>
                                </div>
                                <div className="score-label">Competitiveness</div>
                            </div>
                            
                            <div className="competitiveness-details">
                                <div className="recommendation-badge">
                                    {dualGame.game_details.competitiveness.recommendation}
                                </div>
                                
                                <div className="analysis-text">
                                    {dualGame.game_details.competitiveness.analysis}
                                </div>
                                
                                <div className="excitement-indicators">
                                    {dualGame.game_details.competitiveness.excitement_indicators?.map((indicator, idx) => (
                                        <span key={idx} className="indicator-tag">
                                            {indicator}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="excitement-footer">
                    <div className="combined-score">
                        Combined Excitement: <span className="score">{Math.round((dualGame.combined_score || dualGame.player_a_score + dualGame.player_b_score || 0))}/20</span>
                    </div>
                    {dualGame.achievements && dualGame.achievements.length > 0 && (
                        <div className="achievements">
                            {dualGame.achievements.slice(0, 2).map((achievement, idx) => (
                                <span key={idx} className={`achievement ${achievement.rarity || 'common'}`}>
                                    {achievement.description || 'Achievement'}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const CoStarSelector = ({ onPlayerSelect, currentPlayer }) => {
        // Ensure currentPlayer is a valid object
        const safeCurrentPlayer = currentPlayer && typeof currentPlayer === 'object' ? currentPlayer : null;
        const [searchTerm, setSearchTerm] = useState('');
        const [searchResults, setSearchResults] = useState([]);
        const [searching, setSearching] = useState(false);

        const searchPlayers = async (query) => {
            if (query.length < 2) {
                setSearchResults([]);
                return;
            }

            setSearching(true);
            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/v1/dev/player-logs/search?query=${encodeURIComponent(query)}&limit=10`
                );

                if (response.ok) {
                    const data = await response.json();
                    // Filter out the current player
                    const filtered = data.results.filter(p => {
                        const playerId = typeof p.id === 'number' || typeof p.id === 'string' ? p.id : null;
                        const currentPlayerId = typeof safeCurrentPlayer?.id === 'number' || typeof safeCurrentPlayer?.id === 'string' ? safeCurrentPlayer.id : null;
                        return playerId !== currentPlayerId;
                    });
                    setSearchResults(filtered);
                }
            } catch (err) {
                console.error('Error searching for players:', err);
            } finally {
                setSearching(false);
            }
        };

        useEffect(() => {
            const timeoutId = setTimeout(() => {
                searchPlayers(searchTerm);
            }, 300);

            return () => clearTimeout(timeoutId);
        }, [searchTerm]);

        return (
            <div className="co-star-selector">
                <h4>🎭 Add Co-Star Player</h4>
                <div className="search-box">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search for a player to add as co-star..."
                        className="player-search-input"
                    />
                    {searching && <div className="search-spinner">🔍</div>}
                </div>

                {searchResults.length > 0 && (
                    <div className="search-results">
                        {searchResults.map((player, index) => (
                            <div
                                key={player.id || index}
                                className="search-result"
                                onClick={() => onPlayerSelect(player)}
                            >
                                <div className="player-info">
                                    <span className="player-name">
                                        {typeof player.name === 'string' 
                                            ? player.name 
                                            : typeof player.name === 'object' 
                                                ? JSON.stringify(player.name) 
                                                : String(player.name || 'Unknown Player')
                                        }
                                    </span>
                                    {player.team && (
                                        <span className="player-team">
                                            {typeof player.team === 'string' 
                                                ? player.team 
                                                : typeof player.team === 'object' && player.team.full_name 
                                                    ? player.team.full_name 
                                                    : 'Unknown Team'
                                            }
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const SeasonSummary = ({ summary }) => {
        if (!summary) return null;

        // Safely access nested properties with defaults
        const mustWatchGames = summary.must_watch_games || 0;
        const totalGames = summary.total_games || 0;
        const legendaryMatchupsCount = summary.legendary_matchups_count || 0;
        const gradeDistribution = summary.grade_distribution || {};
        const tierDistribution = summary.tier_distribution || {};
        const totalAchievements = summary.total_achievements || 0;

        return (
            <div className="season-summary">
                <h3>🏀 Season Overview</h3>
                <div className="summary-grid">
                    <div className="summary-card">
                        <div className="summary-number">{mustWatchGames}</div>
                        <div className="summary-label">Must-Watch Games</div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-number">{totalGames}</div>
                        <div className="summary-label">Total Games</div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-number">{legendaryMatchupsCount}</div>
                        <div className="summary-label">Legendary Matchups</div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-number">{totalAchievements}</div>
                        <div className="summary-label">Total Achievements</div>
                    </div>
                </div>

                {Object.keys(gradeDistribution).length > 0 && (
                    <div className="grade-breakdown">
                        <h4>📊 Grade Distribution</h4>
                        <div className="grade-stats">
                            {Object.entries(gradeDistribution).map(([grade, count]) => (
                                <div key={grade} className="grade-item">
                                    <span className={`grade-badge grade-${grade.toLowerCase()}`}>{grade}</span>
                                    <span className="grade-count">{count} games</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {Object.keys(tierDistribution).length > 0 && (
                    <div className="tier-breakdown">
                        <h4>🔥 Watchability Tiers</h4>
                        <div className="tier-stats">
                            {Object.entries(tierDistribution).map(([tier, count]) => (
                                <div key={tier} className="tier-item">
                                    <span className={`tier-badge tier-${tier.replace('_', '-')}`}>{tier.replace('_', ' ')}</span>
                                    <span className="tier-count">{count} games</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="nba-time-machine loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>🔍 Discovering legendary matchups...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="nba-time-machine">
            <div className="time-machine-header">
                <h2>🏀 NBA Time Machine</h2>
                <p className="spoiler-warning">
                    Spoiler-Free Zone - Game outcomes hidden
                </p>
                
                <div className="controls">
                    <div className="season-selector">
                        <label>Season:</label>
                        <select 
                            value={selectedSeason} 
                            onChange={(e) => setSelectedSeason(e.target.value)}
                        >
                            {seasons.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="season-type-selector">
                        <label>Type:</label>
                        <select 
                            value={seasonType} 
                            onChange={(e) => setSeasonType(e.target.value)}
                        >
                            {seasonTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    <div className="dual-player-toggle">
                        <button 
                            className={`theater-toggle ${isDualPlayerMode ? 'active' : ''}`}
                            onClick={() => {
                                if (isDualPlayerMode) {
                                    // Exiting dual player mode - reset everything
                                    setIsDualPlayerMode(false);
                                    setCoStarPlayer(null);
                                    setTheaterData(null);
                                    setActiveMode('matchups');
                                } else {
                                    // Entering dual player mode
                                    setIsDualPlayerMode(true);
                                    if (!coStarPlayer) {
                                    setActiveMode('co_star_search');
                                    }
                                }
                            }}
                            title="Enable Dual-Player Theater Mode"
                        >
                            🎭 {isDualPlayerMode ? 'Theater Mode ON' : 'Add Co-Star'}
                        </button>
                    </div>
                </div>



                {isDualPlayerMode && coStarPlayer && (
                    <div className="theater-mode-header">
                        <div className="co-star-display">
                            <span className="primary-player">{player.name}</span>
                            <span className="theater-vs">×</span>
                            <span className="co-star-player">{coStarPlayer.name}</span>
                            <button 
                                className="remove-co-star"
                                onClick={() => {
                                    setCoStarPlayer(null);
                                    setTheaterData(null);
                                    setIsDualPlayerMode(false);
                                    setActiveMode('matchups');
                                }}
                                title="Remove co-star"
                            >
                                ×
                            </button>
                        </div>
                        <div className="theater-subtitle">🎭 Dual-Player Theater Mode</div>
                    </div>
                )}
            </div>

            {loading && (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>🔍 Discovering legendary matchups...</p>
                </div>
            )}

            {error && (
                <div className="error-message">
                    <h3>⚠️ Discovery Error</h3>
                    <p>{error}</p>
                    <button onClick={fetchEnhancedData} className="retry-btn">
                        🔄 Try Again
                    </button>
                </div>
            )}

            {!loading && !error && !enhancedData && (
                <div className="no-data-message">
                    <p>No data available for this player and season</p>
                </div>
            )}

            {!loading && !error && enhancedData && (
                <>
                    <div className="mode-tabs">
                        {!isDualPlayerMode ? (
                            <>
                                <button 
                                    className={`tab ${activeMode === 'summary' ? 'active' : ''}`}
                                    onClick={() => setActiveMode('summary')}
                                >
                                    📊 Season Overview
                                </button>
                                <button 
                                    className={`tab ${activeMode === 'highlights' ? 'active' : ''}`}
                                    onClick={() => setActiveMode('highlights')}
                                >
                                    🔥 Must-Watch Games
                                </button>
                                <button 
                                    className={`tab ${activeMode === 'matchups' ? 'active' : ''}`}
                                    onClick={() => setActiveMode('matchups')}
                                >
                                    ⚔️ Legendary Matchups
                                </button>
                                <button 
                                    className={`tab ${activeMode === 'discovery' ? 'active' : ''}`}
                                    onClick={() => setActiveMode('discovery')}
                                >
                                    🎯 Discovery Modes
                                </button>
                                <button 
                                    className={`tab ${activeMode === 'dynamic_overview' ? 'active' : ''}`}
                                    onClick={() => setActiveMode('dynamic_overview')}
                                >
                                    📈 Season Insights
                                </button>
                                <button 
                                    className={`tab ${activeMode === 'enhanced_logs' ? 'active' : ''}`}
                                    onClick={() => setActiveMode('enhanced_logs')}
                                >
                                    📋 All Games
                                </button>
                            </>
                        ) : (
                            <>
                                {!coStarPlayer ? (
                                    <button 
                                        className={`tab ${activeMode === 'co_star_search' ? 'active' : ''}`}
                                        onClick={() => setActiveMode('co_star_search')}
                                    >
                                        🔍 Search Co-Star
                                    </button>
                                ) : (
                                    <>
                                        <button 
                                            className={`tab ${activeMode === 'entertainment_rankings' ? 'active' : ''}`}
                                            onClick={() => setActiveMode('entertainment_rankings')}
                                        >
                                            🏆 Entertainment Rankings
                                        </button>
                                        <button 
                                            className={`tab ${activeMode === 'narrative_battles' ? 'active' : ''}`}
                                            onClick={() => setActiveMode('narrative_battles')}
                                        >
                                            🎭 Story Lines
                                        </button>
                                        <button 
                                            className={`tab ${activeMode === 'must_watch' ? 'active' : ''}`}
                                            onClick={() => setActiveMode('must_watch')}
                                        >
                                            🔥 Must Watch
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    <div className="mode-content">
                {activeMode === 'summary' && (
                    <SeasonSummary summary={enhancedData.season_summary} />
                )}

                {activeMode === 'highlights' && (
                    <div className="highlights-mode">
                        <h3>🔥 Enhanced Must-Watch Games</h3>
                        <p>{enhancedData.enhanced_must_watch?.description || `The most compelling ${player.name} performances with rich context`}</p>
                        
                        {/* Show Enhanced Must-Watch Games */}
                        {enhancedData.enhanced_must_watch && (
                            <div className="enhanced-must-watch-section">
                                <div className="must-watch-summary">
                                    <span className="total-count">{enhancedData.enhanced_must_watch.total_count} games</span>
                                    {enhancedData.enhanced_must_watch.categories && (
                                        <span className="categories-count">
                                            {Object.keys(enhancedData.enhanced_must_watch.categories).length} categories
                                        </span>
                                    )}
                                </div>
                                
                                <div className="must-watch-grid">
                                    {(enhancedData.enhanced_must_watch.top_games || []).map((game, idx) => (
                                        <EnrichedMustWatchCard key={idx} game={game} showAnalysis={true} />
                                    ))}
                                </div>
                                
                                {/* Show Categories */}
                                {enhancedData.enhanced_must_watch.categories && (
                                    <div className="must-watch-categories">
                                        <h4>📂 Categories</h4>
                                        <div className="categories-grid">
                                            {Object.entries(enhancedData.enhanced_must_watch.categories).map(([key, category]) => (
                                                <div key={key} className="category-card">
                                                    <h5>{category.name}</h5>
                                                    <p>{category.description}</p>
                                                    <span className="category-count">{category.count} games</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Fallback to old data if new data not available */}
                        {!enhancedData.enhanced_must_watch && (
                        <div className="must-watch-grid">
                            {(enhancedData.season_highlights || []).map((game, idx) => (
                                    <EnrichedMustWatchCard key={idx} game={game} />
                            ))}
                        </div>
                        )}
                    </div>
                )}

                {activeMode === 'matchups' && (
                    <div className="matchups-mode">
                        <h3>⚔️ Legendary Matchups</h3>
                        <p>Epic battles vs other stars during their primes</p>
                        
                        {/* Tag Filters - Only for Legendary Matchups */}
                        <div className="tag-filters">
                            <div className="filter-header">
                                <h4>🏷️ Filter by Tags</h4>
                                <div className="filter-header-controls">
                                    {Object.values(activeTagFilters).some(filters => filters.length > 0) && (
                                        <button className="clear-filters-btn" onClick={clearAllFilters}>
                                            Clear All
                                        </button>
                                    )}
                                    <button 
                                        className="expand-filters-btn" 
                                        onClick={() => setShowAllFilters(!showAllFilters)}
                                    >
                                        {showAllFilters ? 'Show Less' : 'More Filters'} {showAllFilters ? '▲' : '▼'}
                                    </button>
                                </div>
                            </div>
                            
                            {/* Primary Filters - Always Visible */}
                            <div className="primary-filters">
                                {Object.entries(filterOptions.primary).map(([category, options]) => (
                                    <div key={category} className="filter-category primary">
                                        <h5>{category === 'matchupTier' ? '🏆 Matchup Tier' : '👤 Career Stage'}</h5>
                                        <div className="filter-tags">
                                            {options.map(option => (
                                                <button
                                                    key={option.id}
                                                    className={`filter-tag ${activeTagFilters[category].includes(option.id) ? 'active' : ''}`}
                                                    onClick={() => toggleTagFilter(category, option.id)}
                                                    title={option.description}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Secondary Filters - Collapsible */}
                            {showAllFilters && (
                                <div className="secondary-filters">
                                    {Object.entries(filterOptions.secondary).map(([category, options]) => (
                                        <div key={category} className="filter-category secondary">
                                            <h5>{category === 'eliteCategory' ? '⭐ Elite Category' :
                                                 category === 'narrativeType' ? '📖 Story Type' :
                                                 '👤 More Career Stages'}</h5>
                                            <div className="filter-tags">
                                                {options.map(option => (
                                                    <button
                                                        key={option.id}
                                                        className={`filter-tag ${activeTagFilters[category === 'careerStageExtended' ? 'careerStage' : category].includes(option.id) ? 'active' : ''}`}
                                                        onClick={() => toggleTagFilter(category === 'careerStageExtended' ? 'careerStage' : category, option.id)}
                                                        title={option.description}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {(() => {
                            const filteredLegendary = (enhancedData.legendary_matchups || []).filter(matchesFilters);
                            const hasActiveFilters = Object.values(activeTagFilters).some(filters => filters.length > 0);
                            
                            if (!enhancedData.legendary_matchups || enhancedData.legendary_matchups.length === 0) {
                                return (
                            <div className="no-matchups-message">
                                <p>🔍 No legendary matchups found for this season.</p>
                                <p>Try a different season when {player.name} faced more elite opponents during peak performances.</p>
                            </div>
                                );
                            }
                            
                            if (hasActiveFilters && filteredLegendary.length === 0) {
                                return (
                                    <div className="no-matchups-message">
                                        <p>🏷️ No legendary matchups match your selected tags.</p>
                                        <p>Try adjusting your filters or clearing them to see all {enhancedData.legendary_matchups.length} legendary matchups.</p>
                                    </div>
                                );
                            }
                            
                            return (
                            <div className="legendary-matchups-grid">
                                    <div className="filter-results-header">
                                        {hasActiveFilters && (
                                            <p className="filter-count">
                                                Showing {filteredLegendary.length} of {enhancedData.legendary_matchups.length} legendary matchups
                                            </p>
                                        )}
                                    </div>
                                    {filteredLegendary.map((game, idx) => (
                                        <EnrichedLegendaryMatchupCard key={idx} game={game} />
                                ))}
                            </div>
                            );
                        })()}
                    </div>
                )}

                {activeMode === 'discovery' && (
                    <AppleDiscoveryModes 
                        discoveryModes={enhancedData.discovery_modes}
                        playerName={player.name}
                        season={selectedSeason}
                    />
                )}

                {activeMode === 'dynamic_overview' && (
                    <AppleSeasonInsights 
                        dynamicOverview={enhancedData.dynamic_overview}
                        playerName={player.name}
                        season={selectedSeason}
                    />
                )}

                {activeMode === 'enhanced_logs' && !isDualPlayerMode && (
                    <div className="enhanced-logs">
                        <h3>📋 Complete Season ({enhancedData.total_games || 0} games)</h3>
                        <div className="games-list">
                            {(enhancedData.enhanced_logs || []).map((game, idx) => (
                                <EnrichedGameCard key={idx} game={game} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Dual-Player Theater Mode Content */}
                {isDualPlayerMode && activeMode === 'co_star_search' && (
                    <div className="co-star-search-mode">
                        <CoStarSelector 
                            onPlayerSelect={(selectedPlayer) => {
                                setCoStarPlayer(selectedPlayer);
                                setActiveMode('entertainment_rankings');
                            }}
                            currentPlayer={player}
                        />
                    </div>
                )}

                {isDualPlayerMode && coStarPlayer && theaterData && (
                    <>
                        {theaterLoading && (
                            <div className="theater-loading">
                                <div className="spinner"></div>
                                <p>🎭 Creating dual-player theater experience...</p>
                            </div>
                        )}

                        {!theaterLoading && theaterData.theater_analysis && (
                            <>
                                {activeMode === 'entertainment_rankings' && (
                                    <div className="theater-category">
                                        <h3>🏆 Entertainment Rankings</h3>
                                        <p>All games featuring both players, ranked by watchability and entertainment value</p>
                                        <div className="dual-games-grid">
                                            {(() => {
                                                // Combine all games and sort by entertainment value
                                                const allGames = [];
                                                const categories = theaterData.theater_analysis.categories || {};
                                                
                                                // Show games from all categories but filter out parallel universe games
                                                // Parallel universe games have " | " in their matchup description
                                                Object.entries(categories).forEach(([catName, games]) => {
                                                    if (Array.isArray(games)) {
                                                        games.forEach(game => {
                                                            const matchupDesc = game.matchup_description || '';
                                                            
                                                            // Skip parallel universe games (they have " | " separator)
                                                            if (matchupDesc.includes(' | ')) {
                                                                return; // Skip this game
                                                            }
                                                            
                                                            const enhancedGame = {
                                                                ...game,
                                                                category: catName,
                                                                entertainment_score: (game.player_a_score || 0) + (game.player_b_score || 0)
                                                            };
                                                            // Avoid duplicates based on date and matchup
                                                            if (!allGames.find(g => g.game_date === game.game_date && g.matchup_description === game.matchup_description)) {
                                                                allGames.push(enhancedGame);
                                                            }
                                                        });
                                                    }
                                                });
                                                
                                                // Sort by entertainment score
                                                const sortedGames = allGames
                                                    .sort((a, b) => b.entertainment_score - a.entertainment_score)
                                                    .slice(0, 20);

                                                if (sortedGames.length === 0) {
                                                    return (
                                                        <div className="no-games-message">
                                                            <h4>🚫 No Dual-Player Games Found</h4>
                                                            <p>No games found featuring both {player.name} and {coStarPlayer.name} in {selectedSeason}.</p>
                                                        </div>
                                                    );
                                                }

                                                return sortedGames.map((game, idx) => (
                                                    <DualPlayerTheaterCard key={idx} dualGame={game} category="entertainment" />
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {activeMode === 'narrative_battles' && (
                                    <div className="theater-category">
                                        <h3>🎭 Story Lines</h3>
                                        <p>Games with compelling narratives and basketball drama</p>
                                        <div className="dual-games-grid">
                                            {(() => {
                                                const allGames = [];
                                                const categories = theaterData.theater_analysis.categories || {};
                                                
                                                // Focus on head-to-head and rival matchups for best stories
                                                ['head_to_head_power_ranking', 'gladiator_battles', 'rival_matchups'].forEach(catName => {
                                                    const games = categories[catName] || [];
                                                    games.forEach(game => {
                                                        const enhancedGame = {
                                                            ...game,
                                                            narrative: generateNarrative(game, player.name, coStarPlayer.name),
                                                            category: catName
                                                        };
                                                        if (!allGames.find(g => g.game_date === game.game_date)) {
                                                            allGames.push(enhancedGame);
                                                        }
                                                    });
                                                });
                                                
                                                return allGames
                                                    .slice(0, 10)
                                                    .map((game, idx) => (
                                                        <DualPlayerTheaterCard key={idx} dualGame={game} category="narrative" showNarrative={true} />
                                                    ));
                                            })()}
                                        </div>
                                        {(() => {
                                            const categories = theaterData.theater_analysis.categories || {};
                                            const hasAnyGames = ['head_to_head_power_ranking', 'gladiator_battles', 'rival_matchups']
                                                .some(cat => categories[cat] && categories[cat].length > 0);
                                            return !hasAnyGames && (
                                                <div className="no-games-message">
                                                    <p>No narrative-rich games found between {player.name} and {coStarPlayer.name} in {selectedSeason}</p>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {activeMode === 'must_watch' && (
                                    <div className="theater-category">
                                        <h3>🔥 Must Watch Games</h3>
                                        <p>The absolute best games to watch from this season</p>
                                        <div className="dual-games-grid">
                                            {(() => {
                                                const allGames = [];
                                                const categories = theaterData.theater_analysis.categories || {};
                                                
                                                // Collect all games and find the absolute best
                                                Object.entries(categories).forEach(([catName, games]) => {
                                                    if (Array.isArray(games)) {
                                                        games.forEach(game => {
                                                            const totalScore = (game.player_a_score || 0) + (game.player_b_score || 0);
                                                            if (totalScore >= 12) { // Must watch threshold
                                                                const enhancedGame = {
                                                                    ...game,
                                                                    watchability: getWatchabilityTier(totalScore),
                                                                    narrative: generateNarrative(game, player.name, coStarPlayer.name),
                                                                    category: catName,
                                                                    entertainment_score: totalScore
                                                                };
                                                                if (!allGames.find(g => g.game_date === game.game_date)) {
                                                                    allGames.push(enhancedGame);
                                                                }
                                                            }
                                                        });
                                                    }
                                                });
                                                
                                                return allGames
                                                    .sort((a, b) => b.entertainment_score - a.entertainment_score)
                                                    .slice(0, 10)
                                                    .map((game, idx) => (
                                                        <DualPlayerTheaterCard key={idx} dualGame={game} category="must_watch" showNarrative={true} showWatchability={true} />
                                                    ));
                                            })()}
                                        </div>
                                        {(() => {
                                            const categories = theaterData.theater_analysis.categories || {};
                                            const hasHighScoreGames = Object.values(categories).some(games => 
                                                Array.isArray(games) && games.some(g => 
                                                    (g.player_a_score || 0) + (g.player_b_score || 0) >= 12
                                                )
                                            );
                                            return !hasHighScoreGames && (
                                                <div className="no-games-message">
                                                    <p>No must-watch caliber games found between {player.name} and {coStarPlayer.name} in {selectedSeason}</p>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* Theater Insights */}
                                {theaterData.theater_analysis.insights && theaterData.theater_analysis.insights.length > 0 && (
                                    <div className="theater-insights">
                                        <h4>🎯 Theater Insights</h4>
                                        <ul>
                                            {theaterData.theater_analysis.insights.map((insight, idx) => (
                                                <li key={idx}>
                                                    {typeof insight === 'string' 
                                                        ? insight 
                                                        : typeof insight === 'object' 
                                                            ? JSON.stringify(insight) 
                                                            : String(insight || '')
                                                    }
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
                    </div>
                </>
            )}

        </div>
    );
};

export default NBATimeMachine;
